/* eslint-disable max-statements */

import { Flex, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { cloneDeep } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { TransitGroup, TransitHouseConsignment, TransitStatus } from '@e-origin/shared';

import { blueCheckMarkIcon, dangerIcon, deleteIcon, downloadIcon, finalizeIcon, layerMultipleIcon, uploadIcon, viewIcon } from '@assets';
import { TableActions, TableColumnProps, UploadFileModal } from '@components';
import { useDownloadHouseConsignmentItems, useUploadHouseConsignmentItems } from '@hooks/transit';

export const useHouseDetailsColumns = ({
  form,
  isArrival,
  openHouseConsignment,
  queryParams,
  editDisabled,
  editDisabledConditionalForArrival,
  selectedRow,
  houseConsigments,
}) => {
  const navigate = useNavigate();
  const uploadHouseConsignmentItemsMutation = useUploadHouseConsignmentItems();
  const downloadHouseConsignmentItemsMutation = useDownloadHouseConsignmentItems();

  const handleHouseDelete = (houseConsignmentIdx: number) => {
    form.setFieldValue(
      'Consignment.HouseConsignment',
      form?.values?.Consignment.HouseConsignment.filter((_, idx) => idx !== houseConsignmentIdx),
    );
    selectedRow.current = null;
  };

  const openDeleteHouseConsignment = (houseConsignmentIdx: number) => {
    modals.openConfirmModal({
      title: 'Confirm house consignment delete',
      children: <Text>Are you sure you want to delete this house consignment?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => handleHouseDelete(houseConsignmentIdx),
    });
  };

  const handleFileSubmit = async (file: File, houseIndexForUpload: number) => {
    const updatedHouseConsignment = await uploadHouseConsignmentItemsMutation.mutateAsync({
      transitId: form?.values?._id,
      houseConsignmentIndex: houseIndexForUpload,
      file,
    });
    if (updatedHouseConsignment) {
      form.setFieldValue(`Consignment.HouseConsignment.${houseIndexForUpload}.ConsignmentItem`, updatedHouseConsignment.ConsignmentItem);
      form.setFieldValue(`Consignment.HouseConsignment.${houseIndexForUpload}.batches`, []);
    }
  };

  const openUploadItems = (houseIndex: number) => {
    modals.open({
      title: 'Upload items',
      children: <UploadFileModal onHide={modals.closeAll} onFileSubmit={(file) => handleFileSubmit(file, houseIndex)} />,
    });
  };

  const actionList = (house: TransitHouseConsignment, houseIndex: number) => {
    const isUnloadPermission = form?.values?.generalInfo?.status === TransitStatus.UNLOAD_PERMISSION;
    const consignmentItems = cloneDeep(form?.values?.Consignment?.HouseConsignment[houseIndex]?.ConsignmentItem ?? []);
    const itemsMissing = consignmentItems?.filter((consignmentItem) => consignmentItem.isMissing);
    const houseMissing = itemsMissing.length === consignmentItems.length;

    const isHouseMissingAllowed = () => isUnloadPermission && house.receivedFromUnloadPermission && consignmentItems.length > 0 && itemsMissing.length === 0;
    const isHouseFoundAllowed = () => isUnloadPermission && house.receivedFromUnloadPermission && consignmentItems.length > 0 && houseMissing;

    return [
      {
        if: isHouseMissingAllowed(),
        icon: dangerIcon,
        text: 'House missing',
        onClick: () => {
          consignmentItems.forEach((_, index) => {
            const itemPath = `Consignment.HouseConsignment.${houseIndex}.ConsignmentItem.${index}.isMissing`;
            form.setFieldValue(itemPath, true);
          });
          form.setFieldValue(`Consignment.HouseConsignment.${houseIndex}.isMissing`, true);

          toast.success('Item marked as missing!');
        },
      },
      {
        if: isHouseFoundAllowed(),
        icon: finalizeIcon,
        text: 'House found',
        onClick: () => {
          consignmentItems.forEach((_, index) => {
            const itemPath = `Consignment.HouseConsignment.${houseIndex}.ConsignmentItem.${index}.isMissing`;
            form.setFieldValue(itemPath, false);
          });
          form.setFieldValue(`Consignment.HouseConsignment.${houseIndex}.isMissing`, false);

          toast.success('Item marked as found!');
        },
      },
      {
        icon: viewIcon,
        text: 'Edit',
        onClick: () => {
          openHouseConsignment(houseIndex);
        },
        disableAfterClick: false,
      },
      {
        icon: layerMultipleIcon,
        text: 'View Goods Items',
        onClick: () => {
          queryParams.current.set('context', 'GOODS');
          queryParams.current.set('houseRef', form?.values?.Consignment?.HouseConsignment?.indexOf(house).toString());
          navigate({ search: queryParams.current.toString() });
        },
      },
      {
        if: (!editDisabled || !editDisabledConditionalForArrival) && !house.receivedFromUnloadPermission,
        icon: deleteIcon,
        text: 'Delete',
        onClick: () => {
          openDeleteHouseConsignment(houseIndex);
        },
      },
    ];
  };

  const secondaryActions = (houseIndex: number) => {
    return [
      {
        icon: downloadIcon,
        text: 'Download items',
        onClick: () => {
          downloadHouseConsignmentItemsMutation.mutate({ transitId: form?.values?._id, transitName: form?.values?.name, houseConsignmentIndex: houseIndex });
        },
        preventDefaultClick: true,
      },
      {
        if: !editDisabled || !editDisabledConditionalForArrival,
        icon: uploadIcon,
        text: 'Upload items',
        onClick: () => {
          openUploadItems(houseIndex);
        },
        preventDefaultClick: true,
      },
    ];
  };

  const columns: TableColumnProps<TransitHouseConsignment>[] = [
    {
      name: 'Consignor Id',
      cell: (row) => form?.values?.Consignment?.Consignor?.identificationNumber || row.Consignor?.identificationNumber,
      sortable: false,
    },
    {
      name: 'Consignor name',
      cell: (row) => form?.values?.Consignment?.Consignor?.name || row.Consignor?.name,
      sortable: false,
    },
    {
      name: 'Consignee Id',
      cell: (row) => form?.values?.Consignment?.Consignee?.identificationNumber || row.Consignee?.identificationNumber,
      sortable: false,
    },
    {
      name: 'Consignee name',
      cell: (row) => form?.values?.Consignment?.Consignee?.name || row.Consignee?.name,
      sortable: false,
    },
    {
      hide: form?.values?.generalInfo.group === TransitGroup.DEPARTURE,
      name: 'Is missing',
      cell: (row) =>
        row.ConsignmentItem?.length && row.ConsignmentItem.filter((item) => !!item.isMissing).length === row.ConsignmentItem.length ? (
          <Flex align="center" justify="center" h={24} w={24}>
            <img src={blueCheckMarkIcon} alt="Is missing" />
          </Flex>
        ) : (
          ''
        ),
      sortable: false,
    },
    {
      name: 'Goods item',
      cell: (row, rowIdx) => <span data-pw={`cell-house-consignment-goods-item-${rowIdx}`}>{row.ConsignmentItem?.length || 0}</span>,
      sortable: false,
    },
    {
      name: 'Country Of Dispatch',
      cell: (row) => form?.values?.Consignment?.countryOfDispatch || row.countryOfDispatch,
      sortable: false,
      hide: isArrival,
    },
    {
      name: 'Reference Number UCR',
      cell: (row) => form?.values?.Consignment?.referenceNumberUCR || row.referenceNumberUCR,
      sortable: false,
      hide: isArrival,
    },
    {
      name: 'Gross mass',
      cell: (row, rowIdx) => <span data-pw={`cell-house-consignment-gross-mass-${rowIdx}`}>{row.grossMass || 'N/A'}</span>,
      sortable: false,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row, rowIndex) => (
        <TableActions
          actions={actionList(row, rowIndex)}
          secondaryActions={secondaryActions(rowIndex)}
          onClick={() => {
            selectedRow.current = {
              houseConsignmentIdx: houseConsigments.indexOf(row),
              houseConsigment: row,
            };
          }}
          disableAfterClick={true}
          data-pw={rowIndex}
        />
      ),
    },
  ];

  return { columns };
};
